import { Divider, Typography } from '@mui/material';
import React from 'react';

import {
  ArrayField,
  ArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  DateField,
  DateInput,
  DeleteWithConfirmButton,
  Edit,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ImageField,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceArrayField,
  ReferenceField,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleFormIterator,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  ChallengeBasicsFragment,
  ChallengeTimeUnit,
  ChallengeType,
  Prefix,
} from '../../graphql/types';
import useCanWrite from '../../hooks/useCanWrite';

import { IDSearch } from '../reusable/IDSearch';
import AutocompleteClientArrayInput from './components/AutocompleteClientArrayInput';
import { DisplayGenres, GenresEdit } from './components/Genres';
import ImageUploadInput from './components/ImageUploadInput';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';
import {
  convertGoal,
  GoalPeriod,
  isWholeNumberGoal,
  transformGoalPeriod,
} from './goalPeriod';

const PostEditToolbar: React.FC = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const ChallengeFilters = [
  <IDSearch label="Title" source="title" alwaysOn />,
  <BooleanInput label="Archived" source="isArchived" defaultValue={false} />,
  <BooleanInput
    label="Exclude Custom Challenges"
    source="excludeCustomChallenges"
    defaultValue
  />,
  <BooleanInput
    label="Exclude Exos Challenges"
    source="excludeExosChallenges"
  />,
];

export const ChallengesList: React.FC = () => (
  <InfiniteList
    filters={ChallengeFilters}
    sort={{ field: 'enabled', order: 'DESC' }}
  >
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="title" />
      <BooleanField source="enabled" />
      <BooleanField source="featured" />
      <DateField source="startDateTime" sortBy="startDate" label="Start Date" />
      <DateField source="endDateTime" sortBy="endDate" label="End Date" />
      <TextField
        source="participants.count"
        label="Participants"
        sortable={false}
      />
      <TextField source="teams.count" label="Teams" sortable={false} />
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const challengeTypes = Object.keys(ChallengeType).map((name) => ({
  name: name === 'ClassCount' ? 'ActivityCount' : name,
  id: name,
}));

const HOURS_TIME_GOAL_MUST_BE_INTEGER =
  'Hour-based goals must be a whole number (no fractions).';

const validateWholeHourGoal =
  (message: string) => (values: Record<string, unknown>) => {
    const errors: Record<string, string> = {};
    if (values.challengeType !== ChallengeType.Time) {
      return errors;
    }
    const timeUnit = values.timeUnit ?? ChallengeTimeUnit.Hours;
    if (timeUnit !== ChallengeTimeUnit.Hours) {
      return errors;
    }
    const goalRaw = values.goal;
    if (goalRaw === '' || goalRaw === null || goalRaw === undefined) {
      return errors;
    }
    const goal =
      typeof goalRaw === 'number' ? goalRaw : Number(goalRaw as string);
    if (!Number.isFinite(goal)) {
      return errors;
    }
    if (!Number.isInteger(goal)) {
      errors.goal = message;
    }
    return errors;
  };

const validateChallengeCreate = validateWholeHourGoal(
  `${HOURS_TIME_GOAL_MUST_BE_INTEGER} Switch to minutes for partial-hour goals.`,
);

const validateChallengeEdit = validateWholeHourGoal(
  HOURS_TIME_GOAL_MUST_BE_INTEGER,
);

const ChallengeTitle: React.FC = () => (
  <FunctionField
    render={(record?: Record<string, any>) =>
      `${record?.title} <${record?.id}>`
    }
  />
);

const GOAL_PERIOD_CHOICES = (['Total', 'Daily', 'Weekly'] as const).map(
  (period) => ({ id: period, name: period }),
);

const GoalInput: React.FC<{ disabled?: boolean; lockTimeUnit?: boolean }> = ({
  disabled,
  lockTimeUnit,
}) => {
  const challengeType = useWatch({ name: 'challengeType' });
  const timeUnit = useWatch({
    name: 'timeUnit',
    defaultValue: ChallengeTimeUnit.Hours,
  });
  const goal = useWatch({ name: 'goal' });
  const startDate = useWatch({ name: 'startDate' });
  const endDate = useWatch({ name: 'endDate' });
  const goalPeriod: GoalPeriod = useWatch({
    name: 'goalPeriod',
    defaultValue: 'Total',
  });
  const { setValue } = useFormContext();

  const onChangeUnit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUnit = event.target.value;
    const numericGoal = typeof goal === 'number' ? goal : Number(goal);

    if (!Number.isFinite(numericGoal)) {
      return;
    }

    if (newUnit === ChallengeTimeUnit.Minutes) {
      setValue('goal', numericGoal * 60);
    } else {
      setValue('goal', Math.round(numericGoal / 60));
    }
  };

  const roundToWhole = isWholeNumberGoal(challengeType, timeUnit);

  const onChangeGoalPeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
    const converted = convertGoal(
      goal === '' || goal === null || goal === undefined ? NaN : Number(goal),
      goalPeriod,
      event.target.value as GoalPeriod,
      startDate,
      endDate,
      roundToWhole,
    );
    if (converted !== null) {
      setValue('goal', converted);
    }
  };

  const totalGoal =
    goalPeriod === 'Total'
      ? null
      : convertGoal(
          Number(goal),
          goalPeriod,
          'Total',
          startDate,
          endDate,
          roundToWhole,
        );

  let goalType = '';
  if (challengeType === 'Distance') {
    goalType = 'Miles';
  } else if (challengeType === 'Time') {
    goalType = `Time (${timeUnit})`;
  } else if (challengeType === 'StepCount') {
    goalType = 'Steps';
  } else if (challengeType === 'GymArrival') {
    goalType = 'Facility Visits';
  }
  const helperText = goalType ? `Goal Type = ${goalType}` : '';
  return (
    <>
      <Typography variant="h6">Challenge Goal</Typography>
      <RadioButtonGroupInput
        source="goalPeriod"
        label="Goal period"
        defaultValue="Total"
        choices={GOAL_PERIOD_CHOICES}
        onChange={onChangeGoalPeriod}
        row
        helperText={
          'Setting a goal period is only for helping with calculating goals. There are not presently "per day" or "per week" challenges.'
        }
        disabled={disabled}
      />
      {(!startDate || !endDate) && (
        <Typography
          variant="caption"
          color="warning"
          style={{ paddingLeft: '14px', paddingBottom: '12px' }}
        >
          Changing the goal period will not recalculate the goal unless a start
          and end date are set.
        </Typography>
      )}
      {challengeType === 'Time' ? (
        <RadioButtonGroupInput
          defaultValue={ChallengeTimeUnit.Hours}
          source="timeUnit"
          choices={[
            { id: ChallengeTimeUnit.Hours, name: ChallengeTimeUnit.Hours },
            { id: ChallengeTimeUnit.Minutes, name: ChallengeTimeUnit.Minutes },
          ]}
          onChange={onChangeUnit}
          disabled={disabled || lockTimeUnit}
        />
      ) : null}
      <NumberInput
        source="goal"
        helperText={helperText}
        required
        InputProps={{ disabled }}
      />
      {totalGoal !== null && (
        <Typography>
          Total Goal: {totalGoal}
          {goalType && ` ${goalType}`}
        </Typography>
      )}
    </>
  );
};

const ChallengeShowActions: React.FC = () => {
  const canWrite = useCanWrite();
  const record = useRecordContext<ChallengeBasicsFragment>();

  const canDelete =
    record?.enabled === false &&
    record.participants.count === 0 &&
    record.challenger === null;

  return (
    <TopToolbar>
      <WriteOnlyEditButton />
      {record && canDelete && canWrite && (
        <DeleteWithConfirmButton
          confirmColor="warning"
          confirmTitle="Delete Challenge"
          confirmContent="Are you sure you want to delete this challenge?"
        />
      )}
    </TopToolbar>
  );
};

const ChallengeShowLayout: React.FC = () => {
  const record = useRecordContext<ChallengeBasicsFragment>();
  return (
    <TabbedShowLayout>
      <Tab label="General">
        <TextField source="id" label="ID" />
        <FunctionField
          label="Challenge type"
          render={(record: any) =>
            record.challengeType === 'ClassCount'
              ? 'ActivityCount'
              : record.challengeType
          }
        />

        <TextField source="title" />
        <TextField source="subtitle" />
        <TextField source="description" fullWidth />

        <DateField source="startDateTime" />
        <DateField source="endDateTime" />

        {record?.challengeType !== 'StepCount' &&
          record?.challengeType !== 'GymArrival' && (
            <DisplayGenres source="qualifyingGenres" />
          )}
        <FunctionField
          source="goal"
          render={(record: any) => {
            if (record.challengeType === 'Distance') {
              return `${record.goal} Miles`;
            } else if (record.challengeType === 'Time') {
              return `${record.goal} ${record.timeUnit}`;
            } else if (record.challengeType === 'StepCount') {
              return `${record.goal} Steps`;
            } else if (record.challengeType === 'GymArrival') {
              return `${record.goal} Visits`;
            }
            return `${record.goal} Activities`;
          }}
        />
        <ReferenceField
          source="challenger.id"
          reference="User"
          label="Custom Challenge Creator"
          emptyText="Exos Challenge, no creator"
          link="show"
        >
          <FunctionField
            render={(record: Record<string, any>) =>
              record &&
              `${record.firstName} ${record.lastName} <${record.email}>`
            }
          />
        </ReferenceField>
        <ImageField source="badge.url" label="Badge" />
        <ImageField source="background.url" label="Background" />
      </Tab>

      <Tab label="Visibility">
        <BooleanField source="enabled" />
        <BooleanField source="featured" />
        <BooleanField source="clientSpecific" />
        <ReferenceArrayField
          source="clients.ids"
          reference="Client"
          label="Clients"
        >
          <WriteOnlyDatagrid>
            <TextField source="name" />
            <ShowButton />
          </WriteOnlyDatagrid>
        </ReferenceArrayField>
      </Tab>
      <Tab label="Teams">
        <BooleanField source="team" />
        <ArrayField source="teams.nodes" label="" fullWidth={false}>
          <WriteOnlyDatagrid bulkActionButtons={false}>
            <TextField source="name" />
            <TextField source="id" label="ID" />
          </WriteOnlyDatagrid>
        </ArrayField>
      </Tab>
    </TabbedShowLayout>
  );
};

export const ChallengeShow: React.FC = () => (
  <Show title={<ChallengeTitle />} actions={<ChallengeShowActions />}>
    <ChallengeShowLayout />
  </Show>
);

const ChallengeEditComponent: React.FC = (props) => {
  const record = useRecordContext<ChallengeBasicsFragment>(props);
  const canEditScheduleAndGoal =
    record?.enabled === false && record.participants.count === 0;
  let scheduleAndGoalHelperText: string | undefined;
  if (record?.enabled) {
    scheduleAndGoalHelperText =
      'Can only be changed before the challenge is enabled';
  } else if (record?.participants.count) {
    scheduleAndGoalHelperText =
      'Cannot be changed after members have joined the challenge';
  }
  return (
    <TabbedForm
      toolbar={<PostEditToolbar />}
      validate={canEditScheduleAndGoal ? validateChallengeEdit : undefined}
    >
      <FormTab label="General">
        <TextField source="id" label="ID" />

        <SelectInput
          source="challengeType"
          choices={challengeTypes}
          InputProps={{ disabled: true }}
        />

        <TextInput source="title" />
        <TextInput source="subtitle" />
        <TextInput source="description" multiline fullWidth />

        <DateInput
          source="startDate"
          disabled={!canEditScheduleAndGoal}
          helperText={scheduleAndGoalHelperText}
        />
        <DateInput
          source="endDate"
          disabled={!canEditScheduleAndGoal}
          helperText={scheduleAndGoalHelperText}
        />

        {record?.challengeType !== 'StepCount' &&
        record?.challengeType !== 'GymArrival' ? (
          <GenresEdit
            source="qualifyingGenres"
            helperText={`If no genres are selected, all activity will be counted.`}
            label="Qualifying Genres"
            disabled
          />
        ) : null}

        <Divider sx={{ color: 'black', padding: '12px' }} />
        <GoalInput disabled={!canEditScheduleAndGoal} lockTimeUnit />
        <Divider sx={{ color: 'black', padding: '12px' }} />

        <ImageUploadInput
          source="badge"
          prefix={Prefix.Challenge}
          label="Badge"
          helperText="Upload a square (1:1 aspect ratio) image"
        />
        <ImageUploadInput
          source="background"
          prefix={Prefix.Challenge}
          label="Background"
        />
      </FormTab>

      <FormTab label="visibility">
        <BooleanInput source="enabled" />
        <BooleanInput source="featured" />
        <BooleanInput label="Archived" source="isArchived" />
        <BooleanInput source="clientSpecific" disabled />
        {record?.clientSpecific ? (
          <AutocompleteClientArrayInput source="clients.ids" label="Clients" />
        ) : null}
      </FormTab>
      {record?.team ? (
        <FormTab label="team">
          <ArrayInput source="teams.nodes">
            <SimpleFormIterator inline disableReordering>
              <TextInput
                source="name"
                helperText={false}
                style={{ margin: '16px 0 8px' }}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
      ) : null}
    </TabbedForm>
  );
};

export const ChallengeEdit: React.FC = () => (
  <Edit
    title={<ChallengeTitle />}
    mutationMode="pessimistic"
    redirect={false}
    transform={transformGoalPeriod}
  >
    <ChallengeEditComponent />
  </Edit>
);

export const ChallengeCreate: React.FC = () => (
  <Create transform={transformGoalPeriod}>
    <SimpleForm validate={validateChallengeCreate}>
      <SelectInput source="challengeType" choices={challengeTypes} required />

      <TextInput source="title" required />
      <TextInput source="subtitle" />
      <TextInput source="description" multiline fullWidth />

      <DateInput source="startDate" required />
      <DateInput source="endDate" required />

      <FormDataConsumer>
        {({ formData }) =>
          formData.challengeType !== 'StepCount' &&
          formData.challengeType !== 'GymArrival' ? (
            <GenresEdit
              source="qualifyingGenres"
              helperText={`If no genres are selected, all activity will be counted.`}
              label="Qualifying Genres"
            />
          ) : null
        }
      </FormDataConsumer>

      <Divider sx={{ my: 2, width: 1 }} />
      <GoalInput />
      <Divider sx={{ my: 2, width: 1 }} />

      <BooleanInput source="clientSpecific" />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.clientSpecific ? (
            <AutocompleteClientArrayInput
              {...rest}
              source="clients.ids"
              label="Clients"
            />
          ) : null
        }
      </FormDataConsumer>
      <BooleanInput source="team" />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.team ? (
            <ArrayInput source="teams.nodes" {...rest}>
              <SimpleFormIterator inline disableReordering>
                <TextInput source="name" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>
          ) : null
        }
      </FormDataConsumer>

      <ImageUploadInput
        source="badge"
        prefix={Prefix.Challenge}
        label="Badge"
        helperText="Upload a square (1:1 aspect ratio) image"
      />
      <ImageUploadInput
        source="background"
        prefix={Prefix.Challenge}
        label="Background"
      />

      <BooleanInput source="enabled" />
      <BooleanInput source="featured" />
    </SimpleForm>
  </Create>
);
